import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  Title,
  Loader,
  Center,
  Text,
  ActionIcon,
  Group,
} from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import {
  GoogleMap,
  useLoadScript,
  MarkerF as Marker,
  OverlayViewF as OverlayView,
  InfoWindowF as InfoWindow,
} from "@react-google-maps/api";
import { db } from "../firebase";
import { ref, onValue } from "firebase/database";
import { FIREBASE_DB_ROOT } from "../App";
import { Order } from "../models/models";
import { geocodeAddress, GeocodeResult } from "../services/geocoding";

// Types
interface LocationData {
  lat: number;
  lng: number;
  fixTime: string;
  lastUpdate: string;
  driverName?: string;
}

interface DeliveryMarker {
  orderId: string;
  address: string;
  customerName: string;
  phone: string;
  orderCount: number;
  timestamp?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

// Constants
const MAP_CONFIG = {
  zoom: 13,
  center: { lat: 43.5445, lng: 16.5078 },
};

const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "75vh",
  minHeight: "400px",
};

// Simple car icon for drivers
const createCarIcon = (color: string) => () => ({
  path: "M5,11L6.5,6.5H17.5L19,11M17.5,16A1.5,1.5 0 0,1 16,14.5A1.5,1.5 0 0,1 17.5,13A1.5,1.5 0 0,1 19,14.5A1.5,1.5 0 0,1 17.5,16M6.5,16A1.5,1.5 0 0,1 5,14.5A1.5,1.5 0 0,1 6.5,13A1.5,1.5 0 0,1 8,14.5A1.5,1.5 0 0,1 6.5,16M18.92,6C18.72,5.42 18.16,5 17.5,5H6.5C5.84,5 5.28,5.42 5.08,6L3,12V20A1,1 0 0,0 4,21H5A1,1 0 0,0 6,20V19H18V20A1,1 0 0,0 19,21H20A1,1 0 0,0 21,20V12L18.92,6Z",
  fillColor: color,
  fillOpacity: 1,
  strokeColor: "#FFFFFF",
  strokeWeight: 1,
  scale: 1.5,
  anchor: new google.maps.Point(12, 12),
});

// House icon for delivery locations
const createHouseIcon = (color: string) => () => ({
  path: "M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z",
  fillColor: color,
  fillOpacity: 1,
  strokeColor: "#000000",
  strokeWeight: 1,
  scale: 1.5,
  anchor: new google.maps.Point(12, 12),
});

// Predefined marker icons
const MARKER_ICONS = {
  redCar: createCarIcon("#DC2626"), // Red car for "Crveni" drivers
  blueCar: createCarIcon("#2563EB"), // Blue car for "Plavi" drivers
  yellowCar: createCarIcon("#EAB308"), // Yellow car for unknown drivers
  house: createHouseIcon("#EAB308"), // Yellow house for deliveries
  green: createHouseIcon("#22C55E"), // Green house option
};

const CAR_NAMES = {
  "7018520260": "Plavi",
  "7018520201": "Crveni",
};

// Function to get the appropriate car icon based on the car name
const getCarIcon = (username: string) => {
  const carName = CAR_NAMES[username as keyof typeof CAR_NAMES];
  if (carName === "Crveni") {
    return MARKER_ICONS.redCar();
  } else if (carName === "Plavi") {
    return MARKER_ICONS.blueCar();
  }
  // Default to yellow car for unknown cars
  return MARKER_ICONS.yellowCar();
};

// Utility functions
const formatTimestamp = (timestamp: string): string => {
  return new Date(timestamp).toLocaleTimeString("en-GB");
};

// Custom Label Component
const UserLabel: React.FC<{
  username: string;
  timestamp: string;
  position: { lat: number; lng: number };
  onClick?: () => void;
}> = ({ username, timestamp, position, onClick }) => (
  <OverlayView position={position} mapPaneName="overlayMouseTarget">
    <div
      style={{
        background: "#4285f4",
        color: "white",
        padding: "8px 10px",
        borderRadius: "6px",
        fontSize: "12px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        pointerEvents: onClick ? "auto" : "none",
        transform: "translate(-50%, -100%)",
        marginTop: "-25px",
        cursor: onClick ? "pointer" : "default",
      }}
      onClick={onClick}
    >
      {username}
    </div>
  </OverlayView>
);

// Simple Map Component with test marker
const MapComponent: React.FC<{
  locations: Record<string, LocationData>;
  deliveryMarkers: DeliveryMarker[];
  showDeliveryOverlays: boolean;
}> = ({ locations, deliveryMarkers, showDeliveryOverlays }) => {
  const [selectedDelivery, setSelectedDelivery] =
    useState<DeliveryMarker | null>(null);

  const onLoad = useCallback((map: google.maps.Map) => {
    console.log("Map loaded successfully");
  }, []);

  console.log(
    "MapComponent rendered with",
    Object.keys(locations).length,
    "locations"
  );

  return (
    <GoogleMap
      mapContainerStyle={MAP_CONTAINER_STYLE}
      center={MAP_CONFIG.center}
      zoom={MAP_CONFIG.zoom}
      onLoad={onLoad}
    >
      {/* Dynamic markers from Firebase */}
      {Object.entries(locations).map(([username, location]) => {
        // Validate coordinates
        if (
          !location ||
          typeof location.lat !== "number" ||
          typeof location.lng !== "number"
        ) {
          console.warn(`Invalid location data for ${username}:`, location);
          return null;
        }

        const position = { lat: location.lat, lng: location.lng };
        const title = `${
          CAR_NAMES[username as keyof typeof CAR_NAMES] || username
        } (${new Date(location.lastUpdate).toLocaleString()})`;

        return (
          <React.Fragment key={username}>
            <Marker
              position={position}
              title={title}
              icon={getCarIcon(username)}
              onLoad={(marker) =>
                console.log(`Marker loaded for ${location.driverName}`, marker)
              }
            />
            {/* Temporarily hidden car overlay labels */}
            {/* <UserLabel
              username={
                CAR_NAMES[username as keyof typeof CAR_NAMES] || username
              }
              timestamp={location.lastUpdate}
              position={position}
            /> */}
          </React.Fragment>
        );
      })}

      {/* Delivery location markers */}
      {deliveryMarkers.map((delivery) => (
        <React.Fragment key={delivery.orderId}>
          <Marker
            position={delivery.coordinates}
            title={`#${delivery.orderCount} - ${delivery.customerName} - ${delivery.address}`}
            icon={MARKER_ICONS.house()}
            onClick={() => setSelectedDelivery(delivery)}
            onLoad={(marker) =>
              console.log(
                `Delivery marker loaded for order ${delivery.orderCount}`,
                marker
              )
            }
          />
          {showDeliveryOverlays && (
            <OverlayView
              position={delivery.coordinates}
              mapPaneName="overlayMouseTarget"
            >
              <div
                style={{
                  background: "#EAB308",
                  color: "white",
                  padding: "6px 10px",
                  borderRadius: "5px",
                  fontSize: "11px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  fontFamily:
                    "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  pointerEvents: "auto",
                  transform: "translate(-50%, -100%)",
                  marginTop: "-30px",
                  cursor: "pointer",
                }}
                onClick={() => setSelectedDelivery(delivery)}
              >
                #{delivery.orderCount} {delivery.customerName}
              </div>
            </OverlayView>
          )}

          {/* InfoWindow for selected delivery */}
          {selectedDelivery &&
            selectedDelivery.orderId === delivery.orderId && (
              <InfoWindow
                position={delivery.coordinates}
                onCloseClick={() => setSelectedDelivery(null)}
                options={{
                  pixelOffset: new google.maps.Size(0, -10),
                  disableAutoPan: false,
                }}
              >
                <div style={{ padding: "8px", minWidth: "180px" }}>
                  <style>{`
                  .gm-ui-hover-effect { display: none !important; }
                  .gm-style-iw-chr { display: none !important; }
                  .gm-style-iw-tc { display: none !important; }
                `}</style>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "0 0 8px 0",
                    }}
                  >
                    <h3
                      style={{ margin: "0", color: "#333", fontSize: "16px" }}
                    >
                      #{delivery.orderCount} {delivery.customerName}
                    </h3>
                    <button
                      onClick={() => setSelectedDelivery(null)}
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "28px",
                        color: "#666",
                        cursor: "pointer",
                        padding: "6px",
                        lineHeight: "1",
                        borderRadius: "4px",
                        outline: "none",
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <p
                    style={{
                      margin: "8px 0 6px 0",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#555",
                    }}
                  >
                    <a
                      href={`tel:${delivery.phone}`}
                      style={{
                        color: "#4285f4",
                        textDecoration: "none",
                        fontWeight: 600,
                        fontSize: "14px",
                      }}
                    >
                      {delivery.phone}
                    </a>
                  </p>
                  <p
                    style={{
                      margin: "6px 0",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#555",
                      lineHeight: "1.4",
                    }}
                  >
                    <a
                      href={`geo:0,0?q=${encodeURIComponent(delivery.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#4285f4",
                        textDecoration: "none",
                        fontWeight: 600,
                        fontSize: "14px",
                        lineHeight: "1.4",
                      }}
                    >
                      {delivery.address}
                    </a>
                  </p>
                  <p
                    style={{
                      marginTop: "8px",
                      fontSize: "14px",
                      fontWeight: 400,
                    }}
                  >
                    {delivery.timestamp?.slice(11)}
                  </p>
                </div>
              </InfoWindow>
            )}
        </React.Fragment>
      ))}

      {/* Fixed location pin with logo */}
      <Marker
        position={{ lat: 43.538663, lng: 16.491745 }}
        title="Superpajo"
        icon={{
          url: "https://superpajo.hr/logo-1024.png",
          scaledSize: new google.maps.Size(40, 40),
          anchor: new google.maps.Point(20, 40),
        }}
        onLoad={(marker) => console.log("Fixed location marker loaded", marker)}
      />
    </GoogleMap>
  );
};

// Render function for Wrapper
// Main Map Container
export const Map: React.FC = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState<Record<string, LocationData>>({});
  const [deliveryMarkers, setDeliveryMarkers] = useState<DeliveryMarker[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeliveryOverlays, setShowDeliveryOverlays] = useState(false);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyDIlskLme0uikkB6lj70woGLt02ptEXK8E",
  });

  useEffect(() => {
    const locationsRef = ref(db, `test/location`);

    const unsubscribe = onValue(locationsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        console.log("Firebase data received:", data);
        setLocations(data);
      } else {
        console.log("No Firebase location data");
        setLocations({});
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch and geocode orders
  useEffect(() => {
    const date = new Date();
    const ordersPath = `${FIREBASE_DB_ROOT}/Narudžbe/${date.getFullYear()}/${
      date.getMonth() + 1
    }/${date.getDate()}`;
    const ordersRef = ref(db, ordersPath);

    const unsubscribe = onValue(ordersRef, async (snapshot) => {
      const data = snapshot.val();
      if (!data) {
        setDeliveryMarkers([]);
        return;
      }

      // Parse orders and filter for delivery orders without deliveredAt
      const orders: Order[] = Object.entries(data).map(([key, value]) => ({
        ...(value as Order),
        OrderId: key,
      }));

      const deliveryOrders = orders.filter(
        (order) =>
          order.Adresa &&
          order.Adresa !== "PREUZIMANJE U OBJEKTU" &&
          !order.DeliveredAt
      );

      console.log(`Found ${deliveryOrders.length} delivery orders to geocode`);

      // Geocode addresses
      const markers: DeliveryMarker[] = [];
      for (const order of deliveryOrders) {
        try {
          const fullAddress = `${order.Adresa} ${
            order.Mjesto || "Solin"
          }, Croatia`;
          const result = await geocodeAddress(fullAddress);

          if ("lat" in result) {
            markers.push({
              orderId: order.OrderId || "",
              address: `${order.Adresa} ${order.Mjesto || "Solin"}`,
              customerName: order.Ime,
              phone: order.Broj,
              orderCount: order.OrderCount,
              timestamp: order.Vrijeme,

              coordinates: {
                lat: result.lat,
                lng: result.lng,
              },
            });
          } else {
            console.warn(`Geocoding failed for ${order.Adresa}:`, result.error);
          }
        } catch (error) {
          console.error(`Error geocoding ${order.Adresa}:`, error);
        }

        // Small delay to avoid rate limiting
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      setDeliveryMarkers(markers);
      console.log(`Successfully geocoded ${markers.length} delivery locations`);
    });

    return () => unsubscribe();
  }, []);

  if (loadError) {
    return (
      <Center>
        <Text color="red">Error loading Google Maps</Text>
      </Center>
    );
  }

  if (!isLoaded || loading) {
    return (
      <Center>
        <Loader variant="dots" />
      </Center>
    );
  }

  return (
    <Container size="xl" py="md">
      <Group align="center" mb="lg">
        <ActionIcon
          variant="subtle"
          size="xl"
          onClick={() => navigate("/")}
          color="gray"
        >
          <IconArrowLeft size={28} />
        </ActionIcon>
        <Title order={3}>Mapa dostavljača</Title>
      </Group>
      {Object.keys(locations).length === 0 ? (
        <Text ta="center" color="dimmed">
          Nema dostupnih lokacija.
        </Text>
      ) : (
        <MapComponent
          locations={locations}
          deliveryMarkers={deliveryMarkers}
          showDeliveryOverlays={showDeliveryOverlays}
        />
      )}
    </Container>
  );
};
